import React from 'react';
import { formatVoucherDuration } from './durationUtil';

/**
 * SimpleTextTemplate — templated text-only voucher (2026-07-20 editable)
 * Layout: A4 landscape 5×7 = 35 voucher/halaman.
 *
 * Props:
 *  - vouchers: array voucher
 *  - companyName: string (brand name)
 *  - batchHeader: React node
 *  - templateSettings: {
 *      prefix_text, label_text, plan_format,
 *      brand_position: 'top'|'footer'|'hide',
 *      font_brand, font_plan, font_label, font_code   (pt)
 *    }
 */

// Default (fallback) — MUST mirror backend defaults in mysql_routes.py
export const SIMPLE_TEXT_DEFAULTS = Object.freeze({
  prefix_text: 'No.',
  label_text: 'Voucher Code',
  plan_format: '{duration} - {price}',
  brand_position: 'footer',
  font_brand: 7.0,
  font_plan: 12.0,
  font_label: 10.0,
  font_code: 16.0,
});

const applyDefaults = (s) => ({ ...SIMPLE_TEXT_DEFAULTS, ...(s || {}) });

const SimpleTextTemplate = ({ vouchers = [], companyName = '', batchHeader, templateSettings }) => {
  const brandName = (companyName || 'HOTSPOT').toUpperCase();
  const cfg = applyDefaults(templateSettings);

  return (
    <div className="print-container-simple print-root" data-testid="simple-text-template">
      <style jsx>{`
        @page {
          size: A4 landscape;
          margin: 0;
        }
        @media print {
          html, body {
            width: 100%;
            height: 100%;
            margin: 0 !important;
            padding: 0 !important;
            background: white !important;
          }
          html *:not(.print-root):not(.print-root *) { visibility: hidden !important; }
          .print-root, .print-root * { visibility: visible !important; }
          .print-root {
            position: absolute !important;
            left: 0 !important;
            top: 0 !important;
            transform: none !important;
            width: 100% !important;
          }
        }
        .print-container-simple {
          width: 297mm;
          min-height: 210mm;
          padding: 4mm;
          margin: 0;
          background: white;
          box-sizing: border-box;
          font-family: 'Courier New', 'Consolas', monospace;
          color: #000;
        }
        .simple-grid {
          display: grid;
          grid-template-columns: repeat(5, 1fr);
          grid-auto-rows: 32mm;
          gap: 1.4mm;
        }
        .simple-card {
          border: 0.6px solid #000;
          background: #fff;
          padding: 1.4mm 2mm;
          box-sizing: border-box;
          display: flex;
          flex-direction: column;
          justify-content: space-between;
          overflow: hidden;
          page-break-inside: avoid;
        }
        .simple-top {
          display: flex;
          justify-content: space-between;
          align-items: baseline;
          font-size: 7.5pt;
          font-weight: 700;
          color: #000;
        }
        .simple-brand-top {
          text-align: center;
          font-size: ${cfg.font_brand}pt;
          font-weight: 800;
          letter-spacing: 0.3mm;
          line-height: 1.15;
          margin: 0.6mm 0 0.4mm 0;
          color: #000;
        }
        .simple-brand-footer {
          text-align: center;
          font-size: ${cfg.font_brand}pt;
          font-weight: 700;
          letter-spacing: 0.2mm;
          line-height: 1.1;
          color: #333;
          margin-top: 0.6mm;
          padding-top: 0.8mm;
          border-top: 0.3px dashed #999;
        }
        .simple-plan {
          text-align: center;
          font-size: ${cfg.font_plan}pt;
          font-weight: 700;
          line-height: 1.15;
          margin: 0.8mm 0;
        }
        .simple-label {
          text-align: center;
          font-size: ${cfg.font_label}pt;
          font-weight: 700;
          color: #333;
          line-height: 1;
          margin-bottom: 0.4mm;
        }
        .simple-code {
          text-align: center;
          font-size: ${cfg.font_code}pt;
          font-weight: 900;
          letter-spacing: 0.5mm;
          line-height: 1.05;
          margin-top: 0.5mm;
          border-top: 0.4px dashed #666;
          padding-top: 1.4mm;
          padding-bottom: 0.3mm;
          word-break: break-all;
          color: #000;
        }
      `}</style>

      {batchHeader}
      <div className="simple-grid">
      {vouchers.map((voucher, index) => {
        const num = String(index + 1).padStart(2, '0');
        const code = voucher.voucher_code || voucher.username || '';
        const dur = formatVoucherDuration(voucher);
        const price = voucher.price ? `Rp. ${Number(voucher.price).toLocaleString('id-ID')}` : '';
        const durPrice = (cfg.plan_format || '{duration} - {price}')
          .replace('{duration}', dur || '')
          .replace('{price}', price || '')
          .replace(/^\s*-\s*|\s*-\s*$/g, '')
          .trim();
        const showBrandTop = cfg.brand_position === 'top';
        const showBrandFooter = cfg.brand_position === 'footer';

        return (
          <div key={voucher.id || index} className="simple-card">
            <div>
              <div className="simple-top">
                <span>{cfg.prefix_text} {num}</span>
              </div>
              {showBrandTop && <div className="simple-brand-top">{brandName}</div>}
              {durPrice && <div className="simple-plan">{durPrice}</div>}
            </div>
            <div>
              <div className="simple-label">{cfg.label_text}</div>
              <div className="simple-code">{code}</div>
            </div>
            {showBrandFooter && <div className="simple-brand-footer">{brandName}</div>}
          </div>
        );
      })}
      </div>
    </div>
  );
};

export default React.memo(SimpleTextTemplate);
